import { dayKey, daysBetween } from './time.js';

/** The prize photo opens a little every day and is complete on this day. */
export const REVEAL_DAYS = 30;

/**
 * Personal reveal day of the prize photo on the Garage: day 1 is the day the person
 * joined (or the season started, if later), counted in the season time zone, capped
 * at REVEAL_DAYS. Each day shows `day / REVEAL_DAYS` of the photo.
 */
export function revealDay(
  joinedAt: Date,
  seasonStartsAt: Date,
  now: Date,
  tz: string,
  total = REVEAL_DAYS,
): number {
  const start = joinedAt > seasonStartsAt ? joinedAt : seasonStartsAt;
  if (now < start) return 1;
  const day = daysBetween(dayKey(start, tz), dayKey(now, tz)) + 1;
  return Math.min(total, Math.max(1, day));
}

/** Day 1 already opens the headlight and front wing (12 % of the car). */
export const REVEAL_HEAD_START = 0.12;

/** Share of the car revealed on a day (0–1): the head start, then evenly to 1 on the last day. */
export function revealShare(day: number, total = REVEAL_DAYS): number {
  if (day <= 0) return 0;
  if (day >= total) return 1;
  return REVEAL_HEAD_START + ((1 - REVEAL_HEAD_START) * (day - 1)) / (total - 1);
}

export interface RevealSettings {
  /** season: from the season start to its end (full photo on the last day), same for
   *  everyone · personal: `days` days from each person's first day. */
  mode: 'season' | 'personal';
  days: number;
}

/** Day and share of the reveal for one person under the season's settings. */
export function revealFor(
  settings: RevealSettings,
  joinedAt: Date,
  season: { startsAt: Date; endsAt: Date },
  now: Date,
  tz: string,
): { day: number; total: number; share: number } {
  if (settings.mode === 'season') {
    // calendar days of the season, both ends included: 07.10 → 22.10 = 16 days
    const total = Math.max(
      1,
      daysBetween(dayKey(season.startsAt, tz), dayKey(season.endsAt, tz)) + 1,
    );
    const day = revealDay(season.startsAt, season.startsAt, now, tz, total);
    return { day, total, share: revealShare(day, total) };
  }
  const total = Math.max(1, Math.round(settings.days));
  const day = revealDay(joinedAt, season.startsAt, now, tz, total);
  return { day, total, share: revealShare(day, total) };
}
